import React, { useState, useContext, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { ClientContext } from "../context/ClientContext";
import { getSalesByDateRange, getTopSellingProducts } from "../api/api";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import DateRangePicker from "../components/DateRangePicker";
import SalesChart from "../components/SalesChart";

const ReportsScreen = ({ navigation }) => {
  const { selectedClient, isLoading: isClientLoading } =
    useContext(ClientContext);

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // 30 days ago
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("sales");
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [error, setError] = useState(null);

  const loadReportData = async () => {
    if (!selectedClient) return;

    setIsLoading(true);
    setError(null);

    try {
      // Format dates for API without timezone issues
      const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);

      console.log(
        "ReportsScreen: Loading data for date range:",
        formattedStartDate,
        "to",
        formattedEndDate
      );

      // Load data based on active tab
      if (activeTab === "sales") {
        const data = await getSalesByDateRange(
          selectedClient.clientId,
          formattedStartDate,
          formattedEndDate
        );
        setSalesData(data);
      } else {
        const data = await getTopSellingProducts(
          selectedClient.clientId,
          formattedStartDate,
          formattedEndDate,
          10 // Limit to 10 products
        );
        setProductsData(data);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setError("Failed to load report data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadReportData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (selectedClient) {
      loadReportData();
    }
  }, [selectedClient, activeTab, startDate, endDate]);

  const handleClientSelect = () => {
    navigation.navigate("ClientSelection");
  };

  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  };

  const handleDateChange = () => {
    if (startDate && endDate) {
      loadReportData();
    }
  };

  // Prepare sales chart data
  const getSalesChartData = () => {
    if (!salesData?.salesByDate || salesData.salesByDate.length === 0) {
      return { data: [], labels: [] };
    }

    // Sort by date
    const sortedData = [...salesData.salesByDate].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    return {
      data: sortedData.map((item) => item.totalsales),
      labels: sortedData.map((item) => {
        // Parse date safely without timezone issues
        const [year, month, day] = item.date.split("-").map(Number);
        const date = new Date(item.date);
        return `${date.getDate()}/${date.getMonth() + 1}`;
      }),
    };
  };

  // Prepare products chart data
  
  const getProductsChartData = () => {
    if (!productsData?.topProducts || productsData.topProducts.length === 0) {
      return { data: [], labels: [] };
    } 
   
    // Sort by sales amount (descending)
    const sortedData = [...productsData.topProducts].sort(
      (a, b) => b.totalamount - a.totalamount
    );

       // Limit to top 5 for better visualization
    const chartData = sortedData.slice(0, 5);

    return {
      data: chartData.map((item) => item.totalamount),
      labels: chartData.map((item) => {
        // Truncate long product names
        const name = item.productdesc;
        return name?.length > 10 ? name.substring(0, 10) + "..." : name;
      }),
    };
  };

  const { data: salesChartData, labels: salesChartLabels } =
    getSalesChartData();

   /* 
  const { data: productsChartData, labels: productsChartLabels } =
    getProductsChartData(); */

  if (isClientLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#3498db" />
        <Text style={styles.loadingText}>Loading client data...</Text>
      </View>
    );
  }

  if (!selectedClient) {
    return (
      <View style={styles.noClientContainer}>
        <Ionicons name="business-outline" size={64} color="#bdc3c7" />
        <Text style={styles.noClientText}>No Client Selected</Text>
        <Text style={styles.noClientSubtext}>
          Please select a client to view their reports.
        </Text>
        <TouchableOpacity
          style={styles.selectClientButton}
          onPress={handleClientSelect}
        >
          <Text style={styles.selectClientButtonText}>Select Client</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        <View style={styles.header}>
          <View>
            <Text style={styles.greeting}>Reports</Text>
            <TouchableOpacity
              style={styles.clientSelector}
              onPress={handleClientSelect}
            >
              <Text style={styles.clientName}>{selectedClient.clientName}</Text>
              <Ionicons name="chevron-down" size={16} color="#3498db" />
            </TouchableOpacity>
          </View>
        </View>
        
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          label="Select Date Range"
        />

        <TouchableOpacity style={styles.applyButton} onPress={handleDateChange}>
          <Text style={styles.applyButtonText}>Apply</Text>
        </TouchableOpacity>

        <View style={styles.tabBar}>
          <TouchableOpacity
            style={[styles.tab, activeTab === "sales" && styles.activeTab]}
            onPress={() => handleTabChange("sales")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "sales" && styles.activeTabText,
              ]}
            >
              Sales
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === "products" && styles.activeTab]}
            onPress={() => handleTabChange("products")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "products" && styles.activeTabText,
              ]}
            >
              Top Products
            </Text>
          </TouchableOpacity>
        </View>

        {error ? (
          <View style={styles.errorContainer}>
            <Ionicons name="alert-circle-outline" size={48} color="#e74c3c" />
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity
              style={styles.retryButton}
              onPress={loadReportData}
            >
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : isLoading ? (
          <View style={styles.loadingSection}>
            <ActivityIndicator size="large" color="#3498db" />
            <Text style={styles.loadingText}>Loading report data...</Text>
          </View>
        ) : activeTab === "sales" ? (
          // Sales Report
          <>
            <View style={styles.summaryCard}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Total Sales</Text>
                <Text style={styles.summaryValue}>
                  {formatCurrency(salesData?.summary?.totalamount || 0)}
                </Text>
              </View>
              <View style={styles.summaryDivider} />
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Invoices</Text>
                <Text style={styles.summaryValue}>
                  {formatNumber(salesData?.summary?.invoicecount || 0)}
                </Text>
              </View>
              <View style={styles.summaryDivider} />
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Avg. Sale</Text>
                <Text style={styles.summaryValue}>
                  {formatCurrency(salesData?.summary?.averageinvoicevalue || 0)}
                </Text>
              </View>
            </View>
            {salesData?.salesByDate?.length > 1 ? (
            <SalesChart
              title="Sales by Date"
              data={salesChartData}
              labels={salesChartLabels}
              type="line"
            />
            ) : null}

            <Text style={styles.sectionTitle}>Daily Sales</Text>
            {salesData?.salesByDate?.length > 0 ? (
              salesData.salesByDate
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((item, index) => (
                  <View key={index} style={styles.salesItem}>
                    <Text style={styles.salesDate}>
                      {formatDate(item.date, "medium")}
                    </Text>
                    <View style={styles.salesDetails}>
                      <Text style={styles.salesAmount}>
                        {formatCurrency(item.totalsales)}
                      </Text>
                      <Text style={styles.salesCount}>
                        {item.invoicecount}{" "}
                        {item.invoicecount === 1 ? "invoice" : "invoices"}
                      </Text>
                    </View>
                  </View>
                ))
            ) : (
              <Text style={styles.noDataText}>
                No sales data for selected period
              </Text>
            )}
          </>
        ) : (
          // Products Report
          <>
            <View style={styles.summaryCard}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Total Products</Text>
                <Text style={styles.summaryValue}>
                  {formatNumber(productsData?.productSummary?.totalproducts || 0)}
                </Text>
              </View>
              <View style={styles.summaryDivider} />
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Units Sold</Text>
                <Text style={styles.summaryValue}>
                  {formatNumber(productsData?.productSummary?.totalunitssold || 0)}
                </Text>
              </View>
            </View>
           
            {/*<SalesChart
              title="Top 5 Products by Sales"
              data={productsChartData}
              labels={productsChartLabels}
              type="bar"
            /> */}


            <Text style={styles.sectionTitle}>Top Products</Text>
            {productsData?.topProducts?.length > 0 ? (
              productsData.topProducts
                .sort((a, b) => b.totalamount - a.totalamount)
                .map((item, index) => (
                  <View key={index} style={styles.productItem}>
                    <View style={styles.productRank}>
                      <Text style={styles.rankText}>{index + 1}</Text>
                    </View>
                    <View style={styles.productDetails}>
                      <Text style={styles.productName}>{item.productdesc}</Text>
                      <Text style={styles.productCategory}>
                        {item.category || "Uncategorized"}
                      </Text>
                    </View>
                    <View style={styles.productSales}>
                      <Text style={styles.productAmount}>
                        {formatCurrency(item.totalamount)}
                      </Text>
                      <Text style={styles.productUnits}>
                        {formatNumber(item.totalquantity)} units
                      </Text>
                    </View>
                  </View>
                ))
            ) : (
              <Text style={styles.noDataText}>
                No product data for selected period
              </Text>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  scrollContent: {
    padding: 16,
  },
  header: {
    marginBottom: 20,
  },
  greeting: {
    fontSize: 14,
    color: "#7f8c8d",
    marginBottom: 4,
  },
  clientSelector: {
    flexDirection: "row",
    alignItems: "center",
  },
  clientName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2c3e50",
    marginRight: 4,
  },
  applyButton: {
    backgroundColor: "#3498db",
    borderRadius: 8,
    padding: 12,
    alignItems: "center",
    marginBottom: 20,
  },
  applyButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "500",
  },
  tabBar: {
    flexDirection: "row",
    backgroundColor: "#fff",
    borderRadius: 8,
    marginBottom: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: "center",
  },
  activeTab: {
    backgroundColor: "#3498db",
  },
  tabText: {
    fontSize: 16,
    color: "#7f8c8d",
    fontWeight: "500",
  },
  activeTabText: {
    color: "#fff",
  },
  summaryCard: {
    flexDirection: "row",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  summaryItem: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryDivider: {
    width: 1,
    backgroundColor: "#e0e0e0",
    marginHorizontal: 8,
  },
  summaryLabel: {
    fontSize: 12,
    color: "#7f8c8d",
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2c3e50",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2c3e50",
    marginTop: 20,
    marginBottom: 12,
  },
  salesItem: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  salesDate: {
    fontSize: 14,
    color: "#2c3e50",
    fontWeight: "500",
  },
  salesDetails: {
    alignItems: "flex-end",
  },
  salesAmount: {
    fontSize: 16,
    color: "#2c3e50",
    fontWeight: "bold",
  },
  salesCount: {
    fontSize: 12,
    color: "#7f8c8d",
    marginTop: 2,
  },
  productItem: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  productRank: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: "#3498db",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  rankText: {
    color: "#fff",
    fontWeight: "bold",
  },
  productDetails: {
    flex: 1,
  },
  productName: {
    fontSize: 14,
    color: "#2c3e50",
    fontWeight: "500",
  },
  productCategory: {
    fontSize: 12,
    color: "#7f8c8d",
  },
  productSales: {
    alignItems: "flex-end",
  },
  productAmount: {
    fontSize: 14,
    color: "#2c3e50",
    fontWeight: "bold",
  },
  productUnits: {
    fontSize: 12,
    color: "#7f8c8d",
  },
  noDataText: {
    fontSize: 14,
    color: "#7f8c8d",
    textAlign: "center",
    marginTop: 20,
    fontStyle: "italic",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
  },
  loadingSection: {
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#7f8c8d",
  },
  errorContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  errorText: {
    fontSize: 16,
    color: "#7f8c8d",
    textAlign: "center",
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: "#3498db",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  retryButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "500",
  },
  noClientContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    padding: 20,
  },
  noClientText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2c3e50",
    marginTop: 16,
  },
  noClientSubtext: {
    fontSize: 16,
    color: "#7f8c8d",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 24,
  },
  selectClientButton: {
    backgroundColor: "#3498db",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  selectClientButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "500",
  },
});

export default ReportsScreen;
